import { Search } from "@outschool/business-rules";
import {
  EntityAvailability,
  SearchFilters as SearchFiltersType
} from "@outschool/gql-backend-generated";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useSearchFiltersString } from "@outschool/ui-components-website";
import { useCallback } from "react";

function mapClassFormatAttributesToExpectedFormat(classFormats: string[]) {
  return classFormats.map(format => {
    switch (format) {
      case "live":
        return "Live online";
      case "self_paced":
        return "Self-Paced";
      default:
        return format;
    }
  });
}

function mapClassSizeAttributesToExpectedFormat(classSizes: string[]) {
  return classSizes.map(size => {
    switch (size) {
      case "one_on_one":
        return "oneOnOne";
      case "group":
        return "smallGroup";
      default:
        return size;
    }
  });
}

function mapAvailabilityToExpectedFormat(
  availabilities: Omit<EntityAvailability, "entityAvailabilityScheduleUid">[]
) {
  if (!availabilities.length) {
    return {
      startAfterTime: null,
      endByTime: null,
      dow: null
    };
  }

  // Find the earliest start time across all availabilities
  const startAfterTime = Math.min(...availabilities.map(av => av.startHour));

  // Find the latest end time across all availabilities
  const endByTime = Math.max(...availabilities.map(av => av.endHour));

  // Get unique days and map them to day strings
  const uniqueDays = [...new Set(availabilities.map(av => av.day))];
  const daysOfWeekWithAvailability = uniqueDays.map(
    day => Search.DAYS_OF_WEEK[day]
  );

  // Join days with commas
  const dow = daysOfWeekWithAvailability.join(",");

  return {
    startAfterTime,
    endByTime,
    dow
  };
}

function getAttributeValues(
  attributes: { key: string; value: string }[],
  key: string
) {
  return attributes.filter(attr => attr.key === key).map(attr => attr.value);
}

export function useActiveLearnerSearchFilters(): typeof getSearchFilters {
  const { getCapacityFiltersFromClassSize } = useSearchFiltersString();

  const getSearchFilters = useCallback(
    (activeLearner: ChildFragmentFragment | null): SearchFiltersType => {
      if (!activeLearner) {
        return {
          age: null,
          theme: null,
          format: null,
          capacityMin: null,
          capacityMax: null,
          startAfterTime: null,
          endByTime: null,
          dow: null
        };
      }

      return {
        ...(activeLearner.age && { age: activeLearner.age.toString() }),
        theme: getAttributeValues(
          activeLearner.attributes,
          "subject_interest"
        ).join(","),
        ...getCapacityFiltersFromClassSize(
          mapClassSizeAttributesToExpectedFormat(
            getAttributeValues(activeLearner.attributes, "class_size")
          )
        ),
        format: mapClassFormatAttributesToExpectedFormat(
          getAttributeValues(activeLearner.attributes, "class_format")
        ).join(","),
        ...(activeLearner.entityAvailabilitySchedule?.entityAvailabilities &&
          mapAvailabilityToExpectedFormat(
            activeLearner.entityAvailabilitySchedule.entityAvailabilities
          ))
      };
    },
    [getCapacityFiltersFromClassSize]
  );

  return getSearchFilters;
}
